import { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

interface AuditLog {
  id: string;
  tenantId: string;
  userId: string;
  action: string;
  entityType: string;
  entityId?: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  user?: {
    email: string;
    firstName: string;
    lastName: string;
  };
  tenant?: {
    name: string;
  };
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  TENANT: 'Tenant',
  USER: 'Utilisateur',
  DEVICE: 'Véhicule',
  DRIVER: 'Conducteur',
  GROUP: 'Groupe',
  GEOFENCE: 'Géozone',
  ALERT_RULE: "Règle d'alerte",
  ALERT: 'Alerte',
  PLAN: 'Plan',
  SUBSCRIPTION: 'Abonnement',
  MAINTENANCE_TYPE: 'Type maintenance',
  MAINTENANCE_RECORD: 'Fiche maintenance',
  MAINTENANCE_SCHEDULE: 'Planning maintenance',
  SCHEDULED_REPORT: 'Rapport planifié',
  ADMIN_ROLE: 'Rôle admin',
  ADMIN_USER: 'Utilisateur admin',
  APPEARANCE: 'Apparence',
  SYSTEM_CONFIG: 'Configuration système',
  SESSION: 'Session',
  CHECKOUT: 'Paiement',
  PROFILE: 'Profil',
  TWO_FACTOR: 'Double authentification',
  TRACKER_PARAMETER: 'Paramètre tracker',
};

export default function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  useEffect(() => {
    loadLogs();
  }, [actionFilter, entityTypeFilter, page]);

  const loadLogs = async () => {
    try {
      const res = await adminApi.getAuditLogs({
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
        page,
        limit,
      });
      const data = res.data?.data || res.data?.logs || res.data;
      setLogs(Array.isArray(data) ? data : []);
      setTotal(res.data?.pagination?.total || res.data?.total || (Array.isArray(data) ? data.length : 0));
    } catch (error) {
      console.error('Erreur chargement logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action: string) => {
    if (action.includes('CREATE') || action.includes('create')) return 'text-green-400';
    if (action.includes('DELETE') || action.includes('delete')) return 'text-red-400';
    if (action.includes('UPDATE') || action.includes('update')) return 'text-yellow-400';
    if (action.includes('LOGIN') || action.includes('login')) return 'text-blue-400';
    if (action.includes('LOGOUT') || action.includes('logout')) return 'text-gray-400';
    return 'text-gray-600';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Journal d'Audit</h1>
          <p className="text-gray-500">Historique des actions sur la plateforme</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            className="flex-1 min-w-[200px] px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="">Toutes les actions</option>
            <option value="LOGIN">Connexions</option>
            <option value="LOGOUT">Deconnexions</option>
            <option value="CREATE">Creations</option>
            <option value="UPDATE">Modifications</option>
            <option value="DELETE">Suppressions</option>
          </select>
          <select
            value={entityTypeFilter}
            onChange={(e) => {
              setEntityTypeFilter(e.target.value);
              setPage(1);
            }}
            className="flex-1 min-w-[200px] px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="">Tous les types</option>
            {Object.entries(ENTITY_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left p-4 text-gray-500 font-medium">Date</th>
              <th className="text-left p-4 text-gray-500 font-medium">Utilisateur</th>
              <th className="text-left p-4 text-gray-500 font-medium">Tenant</th>
              <th className="text-left p-4 text-gray-500 font-medium">Action</th>
              <th className="text-left p-4 text-gray-500 font-medium">Ressource</th>
              <th className="text-left p-4 text-gray-500 font-medium">IP</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="p-4 text-gray-500 text-sm whitespace-nowrap">
                  {formatDate(log.createdAt)}
                </td>
                <td className="p-4">
                  {log.user ? (
                    <div>
                      <p className="text-gray-900 text-sm">
                        {log.user.firstName} {log.user.lastName}
                      </p>
                      <p className="text-gray-400 text-xs">{log.user.email}</p>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">Système</span>
                  )}
                </td>
                <td className="p-4 text-gray-500 text-sm">{log.tenant?.name || '-'}</td>
                <td className="p-4">
                  <span className={`font-mono text-sm ${getActionColor(log.action)}`}>
                    {log.action}
                  </span>
                </td>
                <td className="p-4">
                  <div>
                    <p className="text-gray-900 text-sm">
                      {ENTITY_TYPE_LABELS[log.entityType] || log.entityType}
                    </p>
                    {log.entityId && (
                      <p className="text-gray-400 text-xs font-mono">{log.entityId}</p>
                    )}
                  </div>
                </td>
                <td className="p-4 text-gray-400 text-xs font-mono">{log.ipAddress || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length === 0 && (
          <div className="text-center py-12 text-gray-500">Aucun log trouvé</div>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-gray-500 text-sm">
            {total} entrees - Page {page} sur {Math.ceil(total / limit)}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="btn-secondary disabled:opacity-50"
            >
              Precedent
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(total / limit)}
              className="btn-secondary disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
